import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography } from '@material-ui/core';
import { resolve } from '../registry/builtins';
import { registerBuiltinComponents } from '../components/builtins';
import { UnknownComponent } from '../components/builtins';
import NodeErrorBoundary from '../components/NodeErrorBoundary';
import ExternalComponentHost from '../host/ExternalComponentHost';
import { buildDatasetMap, mergeNodeProps } from '../bindings/resolveBindings';
import {
  initialStateFromDeclarations,
  runActions,
} from '../interactions/runActions';

let builtinsRegistered = false;
function ensureBuiltins() {
  if (!builtinsRegistered) {
    registerBuiltinComponents();
    builtinsRegistered = true;
  }
}

function getPath(obj, path) {
  return String(path).split('.').reduce((acc, p) => (acc == null ? undefined : acc[p]), obj);
}

function RenderNode({
  node,
  datasetMap,
  pageContext,
  state,
  validationErrors,
  onDispatch,
  schemas,
}) {
  if (!node || typeof node !== 'object') return null;
  const version = node.version || '1.0.0';
  const defn = resolve(node.component, version);
  const { props, meta } = mergeNodeProps(node, datasetMap, pageContext);

  // Bind form field values from page state when name/statePath present
  if (props.name && state) {
    const fromState = getPath(state, props.name) ?? getPath(state, `form.${props.name}`);
    if (fromState !== undefined && props.value === undefined && props.checked === undefined) {
      if (typeof fromState === 'boolean') props.checked = fromState;
      else props.value = fromState;
    }
  }
  if (props.statePath && state) {
    const v = getPath(state, props.statePath);
    if (v !== undefined) {
      if (typeof v === 'boolean') props.checked = v;
      else props.value = v;
    }
  }

  const handleEvent = (eventName, eventPayload) => {
    const actions = node.events && node.events[eventName];
    if (actions && actions.length) {
      onDispatch(actions, eventPayload);
    }
  };

  const fieldError =
    (props.name && validationErrors && (validationErrors[props.name] || validationErrors[`form.${props.name}`])) ||
    null;

  const children = (node.children || []).map((child) => (
    <RenderNode
      key={child.id}
      node={child}
      datasetMap={datasetMap}
      pageContext={pageContext}
      state={state}
      validationErrors={validationErrors}
      onDispatch={onDispatch}
      schemas={schemas}
    />
  ));

  const fallbackNode = node.fallback ? (
    <RenderNode
      node={node.fallback}
      datasetMap={datasetMap}
      pageContext={pageContext}
      state={state}
      validationErrors={validationErrors}
      onDispatch={onDispatch}
      schemas={schemas}
    />
  ) : null;

  if (!defn) {
    return (
      <NodeErrorBoundary nodeId={node.id}>
        <UnknownComponent componentId={node.component} nodeId={node.id} />
      </NodeErrorBoundary>
    );
  }

  if (!defn.runtimeImplemented || defn.runtime === 'sandbox-iframe') {
    return (
      <NodeErrorBoundary nodeId={node.id}>
        <ExternalComponentHost
          definition={defn}
          nodeId={node.id}
          fallback={fallbackNode}
        />
      </NodeErrorBoundary>
    );
  }

  const Comp = defn.Component;
  if (!Comp) {
    return (
      <NodeErrorBoundary nodeId={node.id}>
        <UnknownComponent componentId={node.component} nodeId={node.id} />
      </NodeErrorBoundary>
    );
  }

  return (
    <NodeErrorBoundary nodeId={node.id}>
      <div data-node-id={node.id} data-component={node.component} data-binding-errors={meta.errors.length || undefined}>
        <Comp
          props={props}
          onEvent={handleEvent}
          error={fieldError}
        >
          {children}
        </Comp>
      </div>
    </NodeErrorBoundary>
  );
}

/**
 * Recursive Workload Pages canvas renderer.
 */
export default function CanvasRenderer({
  canvas,
  manifest,
  datasets,
  schemas,
}) {
  ensureBuiltins();

  const defaults = useMemo(
    () => initialStateFromDeclarations(canvas && canvas.state),
    [canvas],
  );
  const [state, setState] = useState(defaults);
  const [validationErrors, setValidationErrors] = useState({});

  const datasetMap = useMemo(
    () => buildDatasetMap(manifest, datasets || {}),
    [manifest, datasets],
  );

  const pageContext = useMemo(
    () => ({
      page: {
        title: canvas && canvas.title,
        subtitle: canvas && canvas.subtitle,
        page_id: canvas && canvas.page_id,
      },
    }),
    [canvas],
  );

  const onDispatch = useCallback(
    (actions, event) => {
      const result = runActions(actions, {
        state,
        defaults,
        event,
        schemas: schemas || (canvas && canvas.validation_schemas) || {},
      });
      setState(result.state);
      setValidationErrors(result.validationErrors || {});
      if (result.navigation && typeof window !== 'undefined') {
        if (String(result.navigation).startsWith('#')) {
          window.location.hash = result.navigation;
        }
      }
    },
    [state, defaults, schemas, canvas],
  );

  if (!canvas || !canvas.root) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">No canvas document</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="workload-canvas-root"
      style={{
        maxWidth: (canvas.theme && canvas.theme.maxWidth) || 1100,
        margin: '0 auto',
        padding: '16px 12px 48px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <RenderNode
        node={canvas.root}
        datasetMap={datasetMap}
        pageContext={pageContext}
        state={state}
        validationErrors={validationErrors}
        onDispatch={onDispatch}
        schemas={schemas || canvas.validation_schemas}
      />
    </Box>
  );
}
